import type { CachedState, InvitedAppShortcutsCache } from '../types';

/** Serializes every `enqueueUpdate` globally; `lastCommittedStateByCache` still scopes `prev` per cache instance. */
const queue: Array<() => void> = [];

/**
 * Last state successfully persisted per cache, from this module's queue only.
 * `cache.read()` can lag behind `cache.write()` (e.g. async cookies), so sequential
 * updaters must not rely on read() alone for `prev`.
 *
 * Updated only after `cache.write` resolves **true** (confirmed persist). Not updated
 * when the updater returns null/`prev`, when `write` resolves false, on throw from the
 * updater or `write`, or in `catch` below — so failed updates do not advance the chain.
 */
const lastCommittedStateByCache = new WeakMap<InvitedAppShortcutsCache, CachedState>();

/**
 * Enqueues a functional update to the cache. The update will be applied after all
 * previously enqueued updates have been processed.
 *
 * Going through this function ensures that multiple updates to the cache are applied sequentially,
 * preventing race conditions.
 *
 * @param cache - The cache to update
 * @param updater - A function that returns a new state to be written to the cache.
 *   It's called with `prev`, which is the latest cache state from the last queued write
 *   (or `cache.read()` when none).
 *   If it returns `null` or `prev`, the cache will not be updated.
 *
 * @returns Resolves to the effective cached state after this update: the new state if a write
 *   succeeded, otherwise the prior `prev` (e.g. updater no-op, or `cache.write` resolved false).
 */
export const enqueueUpdate = (
	cache: InvitedAppShortcutsCache,
	updater: (prev: CachedState) => CachedState | null,
): Promise<CachedState> => {
	let resolve: (result: CachedState) => void;
	let reject: (result: unknown) => void;
	const promise = new Promise<CachedState>((res, rej) => {
		resolve = res;
		reject = rej;
	});
	const processUpdate = async () => {
		const prev = lastCommittedStateByCache.get(cache) ?? cache.read({ ssr: false }) ?? {};
		try {
			const result = updater(prev);
			if (result === null || result === prev) {
				resolve(prev);
			} else {
				const writeSucceeded = await cache.write(result);
				if (writeSucceeded) {
					lastCommittedStateByCache.set(cache, result);
					resolve(result);
				} else {
					// Do not set lastCommittedStateByCache — storage may not match `result`.
					resolve(prev);
				}
			}
		} catch (error) {
			// Do not update lastCommittedStateByCache; queue still advances below.
			reject(error);
		}
		queue.shift(); // Remove the processed update
		if (queue.length > 0) {
			const nextProcessUpdate = queue[0];
			nextProcessUpdate!(); // Start the next update, keep it in the queue (blocking) until processed
		}
	};
	queue.push(processUpdate);
	if (queue.length === 1) {
		processUpdate();
	}
	return promise;
};
